import React, { useId, useState } from "react";

import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Close } from "@gaming/xds-react-js-icons";

import { Modal } from "../../../../components/Modal/Modal";
import { useStrings } from "../../../../localization/localizationHelpers";
import type { AdminContact } from "../../../../models/adminPage";
import { getAvatarClass, getMemberInitials } from "../../avatarHelpers";
import { getFuzzySearchScore } from "../../fuzzySearch";
import * as modalStyles from "../AdminActionConfirmationModal/AdminActionConfirmationModal.module.css";
import * as roleStyles from "../InviteContactModal/InviteContactModal.module.css";

type TransferPrimarySignatoryModalProps = Readonly<{
  contacts: readonly AdminContact[];
  isBusy: boolean;
  isOpen: boolean;
  onCancel: () => void;
  onSelect: (contact: AdminContact) => void;
}>;

export const TransferPrimarySignatoryModal = ({
  contacts,
  isBusy,
  isOpen,
  onCancel,
  onSelect,
}: TransferPrimarySignatoryModalProps) => {
  const titleId = useId();
  const resultsId = useId();
  const [searchQuery, setSearchQuery] = useState("");
  const [targetId, setTargetId] = useState("");
  const [title, description, targetLabel, confirmLabel, noResultsLabel, cancelLabel, closeLabel] =
    useStrings(
      "partnerAdminTransferPrimarySignatoryTitle",
      "partnerAdminTransferPrimarySignatoryDescription",
      "partnerAdminTransferPrimarySignatoryTargetLabel",
      "partnerAdminTransferPrimarySignatoryConfirmLabel",
      "noResultsFound",
      "partnerAdminInviteCancelLabel",
      "partnerAdminConfirmationCloseLabel"
    );
  const matchingContacts = searchQuery.trim()
    ? contacts
        .map(contact => ({
          contact,
          searchScore: getFuzzySearchScore(searchQuery, [
            contact.firstName,
            contact.lastName,
            `${contact.firstName} ${contact.lastName}`,
            contact.email,
            contact.jobTitle,
          ]),
        }))
        .filter(
          (
            match
          ): match is {
            contact: AdminContact;
            searchScore: number;
          } => match.searchScore !== undefined
        )
        .sort((left, right) => left.searchScore - right.searchScore)
        .map(match => match.contact)
        .slice(0, 5)
    : [];

  const close = () => {
    setSearchQuery("");
    setTargetId("");
    onCancel();
  };

  return (
    <Modal isOpen={isOpen} ariaLabelledBy={titleId} onClose={isBusy ? () => undefined : close}>
      <div className={modalStyles.modalBody} aria-busy={isBusy}>
        <div className={modalStyles.header}>
          <h2 id={titleId} className={modalStyles.title}>
            {title}
          </h2>
          <button
            type="button"
            className={modalStyles.close}
            aria-label={closeLabel}
            disabled={isBusy}
            onClick={close}
          >
            <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
          </button>
        </div>
        <p className={modalStyles.description}>{description}</p>
        <label className={roleStyles.field}>
          <span>{targetLabel}</span>
          <input
            type="search"
            role="combobox"
            aria-label={targetLabel}
            aria-autocomplete="list"
            aria-controls={resultsId}
            aria-expanded={Boolean(searchQuery)}
            aria-activedescendant={targetId ? `${resultsId}-${targetId}` : undefined}
            value={searchQuery}
            disabled={isBusy}
            onChange={event => {
              setSearchQuery(event.target.value);
              setTargetId("");
            }}
          />
        </label>
        {searchQuery &&
          (matchingContacts.length > 0 ? (
            <div
              id={resultsId}
              className={roleStyles.roleOptions}
              role="listbox"
              aria-label={targetLabel}
            >
              {matchingContacts.map(contact => {
                const name = `${contact.firstName} ${contact.lastName}`.trim() || contact.email;
                return (
                  <button
                    id={`${resultsId}-${contact.contactId}`}
                    key={contact.contactId}
                    type="button"
                    role="option"
                    className={modalStyles.memberCard}
                    aria-label={name}
                    aria-selected={targetId === contact.contactId}
                    disabled={isBusy}
                    onClick={() => setTargetId(contact.contactId)}
                  >
                    <div
                      className={`${modalStyles.avatar} ${getAvatarClass(contact.contactId)}`}
                      aria-hidden="true"
                    >
                      {getMemberInitials(contact)}
                    </div>
                    <span className={modalStyles.identity}>
                      <strong className={modalStyles.name}>{name}</strong>
                      <span className={modalStyles.email}>{contact.email}</span>
                      {contact.jobTitle && (
                        <span className={modalStyles.email}>{contact.jobTitle}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className={modalStyles.description}>{noResultsLabel}</p>
          ))}
        <div className={modalStyles.actions}>
          <BasicButton
            as="button"
            buttonType={ButtonType.Secondary}
            type="button"
            disabled={isBusy}
            onClick={close}
          >
            {cancelLabel}
          </BasicButton>
          <BasicButton
            as="button"
            buttonType={ButtonType.Brand}
            type="button"
            disabled={isBusy || !targetId}
            onClick={() => {
              const target = contacts.find(contact => contact.contactId === targetId);
              if (target) {
                setSearchQuery("");
                setTargetId("");
                onSelect(target);
              }
            }}
          >
            {confirmLabel}
          </BasicButton>
        </div>
      </div>
    </Modal>
  );
};
